/**
 * Shared data layer for browsing property listings via GET /properties/listings.
 * Used by both PropertyListings.jsx and SearchResults.jsx so filter/query-param
 * mapping and response parsing live in exactly one place.
 */

import { useState, useCallback, useEffect } from 'react';
import { request } from '../services/apiClient';

const buildQueryString = (filters) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
        if (value === undefined || value === null || value === '') return;
        if (Array.isArray(value)) {
            if (value.length > 0) params.append(key, value.join(','));
        } else {
            params.append(key, value.toString());
        }
    });
    return params.toString();
};

export const usePropertyListings = (initialFilters = {}) => {
    const [filters, setFilters] = useState(initialFilters);
    const [properties, setProperties] = useState([]);
    const [pagination, setPagination] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchListings = useCallback(async (activeFilters) => {
        setLoading(true);
        setError(null);
        try {
            const query = buildQueryString(activeFilters);
            const data = await request(`/properties/listings?${query}`);
            setProperties(data.data?.properties || []);
            setPagination(data.data?.pagination || {});
        } catch (err) {
            setError(err.message);
            setProperties([]);
            setPagination({});
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchListings(filters);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const updateFilters = useCallback((newFilters) => {
        setFilters(newFilters);
        fetchListings(newFilters);
    }, [fetchListings]);

    return {
        properties,
        pagination,
        loading,
        error,
        filters,
        updateFilters,
        refetch: () => fetchListings(filters)
    };
};
